import React,{useState} from 'react';
import {motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import {useLanguage} from '../context/LanguageContext';
import {FaUsers,FaClock,FaGraduationCap,FaHome,FaBook,FaPlay,FaExternalLinkAlt} from 'react-icons/fa';

const CourseCard=({course})=> {
  const {t,language}=useLanguage();

  // ✅ NUOVO: Apri direttamente in nuova tab invece del modal
  const handleCourseClick=()=> {
    // Estrai l'URL del corso dall'iframe
    const iframeMatch=course.iframe.match(/src="([^"]+)"/);
    if (iframeMatch && iframeMatch[1]) {
      const courseUrl=iframeMatch[1];
      console.log('🎓 Opening course in new tab:',courseUrl);
      window.open(courseUrl,'_blank','noopener,noreferrer');
    } else {
      console.error('❌ Could not extract course URL from iframe');
    }
  };

  return (
    <motion.div
      initial={{opacity: 0,y: 20}}
      animate={{opacity: 1,y: 0}}
      className="bg-white rounded-lg shadow-lg overflow-hidden relative"
    >
      <div className="relative">
        <img
          src={course.image}
          alt={course.title}
          className="w-full h-48 object-cover"
        />
        {/* Language badge */}
        <div className="absolute bottom-4 left-4 bg-white px-2 py-1 rounded-full text-xs font-bold flex items-center">
          {course.language==='en' ? (
            <>
              <span className="mr-1">🇺🇸</span>
              <span>EN</span>
            </>
          ) : (
            <>
              <span className="mr-1">🇮🇹</span>
              <span>IT</span>
            </>
          )}
        </div>
        {/* Status Badge */}
        <div className="absolute top-4 right-4 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
          {language==='en' ? 'Available' : 'Disponibile'}
        </div>
        <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
          <button
            className="bg-white rounded-full p-4 text-indigo-600 hover:text-indigo-800 transition-colors flex items-center gap-2"
            onClick={handleCourseClick}
          >
            <FaExternalLinkAlt className="text-xl" />
          </button>
        </div>
      </div>
      <div className="p-6">
        <h3 className="text-xl font-bold mb-2">{course.title}</h3>
        <p className="text-gray-600 mb-4 text-sm leading-relaxed h-16 overflow-hidden">
          {course.description}
        </p>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <FaClock className="text-gray-400 mr-1" />
            <span>{course.duration} {course.duration===1 ? t('hour') : t('hours')}</span>
          </div>
          <div className="text-lg font-bold text-indigo-600">${course.price}</div>
        </div>
        {/* ✅ AGGIORNATO: Pulsante con icona esterna */}
        <button
          onClick={handleCourseClick}
          className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors flex items-center justify-center gap-2"
        >
          <FaExternalLinkAlt className="text-sm" />
          {language==='en' ? 'Access Course' : 'Accedi al Corso'}
        </button>
        {/* ✅ RIMOSSO: Box ridondante sui pagamenti */}
      </div>
    </motion.div>
  );
};

const Courses=()=> {
  const {t,language,toggleLanguage}=useLanguage();

  // Corsi disponibili unificati con la stessa struttura
  const courses=[
    // PID-5-BF+ - Disponibile in inglese
    {
      id: 'pid5bf-plus',
      title: "PID-5-BF+",
      description: language==='en' ? "50-minute training course on using the PID-5-BF+ to assess personality traits according to DSM-5 AMPD model." : "Corso di formazione di 50 minuti sull'uso del PID-5-BF+ per valutare i tratti di personalità secondo il modello DSM-5 AMPD.",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751009570247-blob",
      duration: 0.5,
      price: 15,
      language: 'en',
      iframe: '<div id="iframe_container" style="width: 100%;position: relative;overflow: hidden;padding-top: 100%;"><iframe id="mcg" name="mcg_frame" title="Mini-Course" src="https://share.minicoursegenerator.com/pid-5-bf-63885873" align="center" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" style="position: absolute;width: 100%;height: 100%;top: 0;right: 0;bottom: 0;left: 0;border: none;margin: 0px;padding: 0px;"></iframe></div>'
    },
    // PID-5-BF+ - Disponibile in italiano (NUOVO)
    {
      id: 'pid5bf-plus-it',
      title: "PID-5-BF+",
      description: "Corso di formazione di 50 minuti sull'uso del PID-5-BF+ per valutare i tratti di personalità secondo il modello DSM-5 AMPD.",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1751009570247-blob",
      duration: 0.5,
      price: 15,
      language: 'it',
      iframe: '<div id="iframe_container" style="width: 100%;position: relative;overflow: hidden;padding-top: 100%;"><iframe id="mcg" name="mcg_frame" title="Mini-Course" src="https://share.minicoursegenerator.com/personality-inventory-disorders-pid-5bf-ita-63889942" align="center" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" style="position: absolute;width: 100%;height: 100%;top: 0;right: 0;bottom: 0;left: 0;border: none;margin: 0px;padding: 0px;"></iframe></div>'
    },
    // Epistemic Trust - Solo in italiano con descrizione aggiornata
    {
      id: 'epistemic-trust',
      title: "Epistemic Trust Mistrust Credulity Questionnaire (ETMCQ)",
      description: "Formazione sul questionario ETMCQ che valuta come le persone elaborano la conoscenza sociale in contesti terapeutici.",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753821913957-ETMCQ.jpeg",
      duration: 0.5,
      price: 15,
      language: 'it',
      iframe: '<div id="iframe_container" style="width: 100%;position: relative;overflow: hidden;padding-top: 100%;"><iframe id="mcg" name="mcg_frame" title="Mini-Course" src="https://share.minicoursegenerator.com/epistemic-trust-mistrust-credulity-questionna-63885162" align="center" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" style="position: absolute;width: 100%;height: 100%;top: 0;right: 0;bottom: 0;left: 0;border: none;margin: 0px;padding: 0px;"></iframe></div>'
    },
    // NUOVO CORSO: Epistemic Trust in inglese
    {
      id: 'epistemic-trust-en',
      title: "Epistemic Trust Mistrust Credulity Questionnaire (ETMCQ)",
      description: "Training on the ETMCQ questionnaire that evaluates how individuals process social knowledge in therapeutic contexts.",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753821913957-ETMCQ.jpeg",
      duration: 0.5,
      price: 15,
      language: 'en',
      iframe: '<div id="iframe_container" style="width: 100%;position: relative;overflow: hidden;padding-top: 100%;"><iframe id="mcg" name="mcg_frame" title="Mini-Course" src="https://share.minicoursegenerator.com/epistemic-trust-mistrust-credulity-questionna-638696837641954760" align="center" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" style="position: absolute;width: 100%;height: 100%;top: 0;right: 0;bottom: 0;left: 0;border: none;margin: 0px;padding: 0px;"></iframe></div>'
    },
    // NUOVO CORSO: Compass of Shame Scale (COSS) in inglese
    {
      id: 'compass-of-shame-scale-en',
      title: "Compass of Shame Scale (COSS)",
      description: "Professional training course on the Compass of Shame Scale designed by Jeff Elison to assess shame management strategies.",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753821708476-Compass%20of%20Shame.jpeg",
      duration: 0.5,
      price: 15,
      language: 'en',
      iframe: '<div id="iframe_container" style="width: 100%;position: relative;overflow: hidden;padding-top: 100%;"><iframe id="mcg" name="mcg_frame" title="Mini-Course" src="https://share.minicoursegenerator.com/compass-of-shame-scale-coss-638767788157875823" align="center" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" style="position: absolute;width: 100%;height: 100%;top: 0;right: 0;bottom: 0;left: 0;border: none;margin: 0px;padding: 0px;"></iframe></div>'
    },
    // COSMO - Disponibile solo in italiano con descrizione aggiornata e immagine aggiornata
    {
      id: 'cosmo-method-it',
      title: "Picture Frustration Study - COSMO",
      description: "Formazione sul sistema COSMO per la Picture Frustration Study con codifica per gravità e stile interpersonale.",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753822628359-Cosmo.jpeg",
      duration: 2,
      price: 50,
      language: 'it',
      iframe: '<div id="iframe_container" style="width: 100%;position: relative;overflow: hidden;padding-top: 100%;"><iframe id="mcg" name="mcg_frame" title="Mini-Course" src="https://share.minicoursegenerator.com/cosmo---il-nuovo-metodo-che-ridefinisce-il-pi-638844100374233581" align="center" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" style="position: absolute;width: 100%;height: 100%;top: 0;right: 0;bottom: 0;left: 0;border: none;margin: 0px;padding: 0px;"></iframe></div>'
    },
    // NUOVO CORSO: Picture Frustration Study - COSMO in inglese con immagine aggiornata
    {
      id: 'picture-frustration-study-en',
      title: "Picture Frustration Study - COSMO",
      description: "Training on the COSMO system for Picture Frustration Study with coding for severity and interpersonal style.",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753822628359-Cosmo.jpeg",
      duration: 2,
      price: 50,
      language: 'en',
      iframe: '<div id="iframe_container" style="width: 100%;position: relative;overflow: hidden;padding-top: 100%;"><iframe id="mcg" name="mcg_frame" title="Mini-Course" src="https://share.minicoursegenerator.com/cosmo---redefining-the-picture-frustration-st-638582281792005421" align="center" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" style="position: absolute;width: 100%;height: 100%;top: 0;right: 0;bottom: 0;left: 0;border: none;margin: 0px;padding: 0px;"></iframe></div>'
    },
    // Compass of Shame Scale - Solo in italiano con descrizione aggiornata
    {
      id: 'compass-of-shame-scale-it',
      title: "Compass of Shame Scale (COSS)",
      description: "Corso di formazione sulla Compass of Shame Scale ideata da Jeff Elison per valutare le strategie di gestione della vergogna.",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753821708476-Compass%20of%20Shame.jpeg",
      duration: 0.5,
      price: 15,
      language: 'it',
      iframe: '<div id="iframe_container" style="width: 100%;position: relative;overflow: hidden;padding-top: 100%;"><iframe id="mcg" name="mcg_frame" title="Mini-Course" src="https://share.minicoursegenerator.com/compass-of-shame-scale-638842361834016585" align="center" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" style="position: absolute;width: 100%;height: 100%;top: 0;right: 0;bottom: 0;left: 0;border: none;margin: 0px;padding: 0px;"></iframe></div>'
    },
    // LPF Scale 2.0 BF - Solo in italiano con descrizione aggiornata
    {
      id: 'lpf-scale-it',
      title: "Level of Personality Scale 2.0 (LPFS-BF 2.0)",
      description: "Formazione sulla Level of Personality Functioning Scale – Brief Form per valutare la gravità dei disturbi di personalità.",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753822040203-LPF%20Scale%202.jpeg",
      duration: 0.5,
      price: 15,
      language: 'it',
      iframe: '<div id="iframe_container" style="width: 100%;position: relative;overflow: hidden;padding-top: 100%;"><iframe id="mcg" name="mcg_frame" title="Mini-Course" src="https://share.minicoursegenerator.com/lpf-scale-20-forma-breve-638841325214282676" align="center" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" style="position: absolute;width: 100%;height: 100%;top: 0;right: 0;bottom: 0;left: 0;border: none;margin: 0px;padding: 0px;"></iframe></div>'
    },
    // NUOVO CORSO: LPF Scale 2.0 BF in inglese - URL corretto
    {
      id: 'lpf-scale-en',
      title: "Level of Personality Scale 2.0 (LPFS-BF 2.0)",
      description: "Training on the Level of Personality Functioning Scale – Brief Form for assessing personality disorder severity.",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753822040203-LPF%20Scale%202.jpeg",
      duration: 0.5,
      price: 15,
      language: 'en',
      iframe: '<div id="iframe_container" style="width: 100%;position: relative;overflow: hidden;padding-top: 100%;"><iframe id="mcg" name="mcg_frame" title="Mini-Course" src="https://share.minicoursegenerator.com/level-of-personality-functioning-scale-20-bf-638767784696637900" align="center" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" style="position: absolute;width: 100%;height: 100%;top: 0;right: 0;bottom: 0;left: 0;border: none;margin: 0px;padding: 0px;"></iframe></div>'
    },
    // Thurston Cradock Test of Shame - Solo in inglese con descrizione aggiornata e NUOVA IMMAGINE
    {
      id: 'thurston-cradock-en',
      title: "Thurston Cradock Test of Shame",
      description: "Training on identifying and exploring shame dynamics using the Thurston Cradock Test of Shame (TCTS).",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753823323275-0001263_thurston_cradock_test_of_shame_tcts_708.jpeg.webp",
      duration: 2,
      price: 50,
      language: 'en',
      iframe: '<div id="iframe_container" style="width: 100%;position: relative;overflow: hidden;padding-top: 100%;"><iframe id="mcg" name="mcg_frame" title="Mini-Course" src="https://share.minicoursegenerator.com/thurston-cradock-test-of-shame-638844968487356061" align="center" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" style="position: absolute;width: 100%;height: 100%;top: 0;right: 0;bottom: 0;left: 0;border: none;margin: 0px;padding: 0px;"></iframe></div>'
    },
    // ✅ NUOVO CORSO: Thurston Cradock Test of Shame in italiano con stessa immagine
    {
      id: 'thurston-cradock-it',
      title: "Thurston Cradock Test of Shame",
      description: "Formazione sull'identificazione ed esplorazione delle dinamiche della vergogna utilizzando il Thurston Cradock Test of Shame (TCTS).",
      image: "https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1753823323275-0001263_thurston_cradock_test_of_shame_tcts_708.jpeg.webp",
      duration: 2,
      price: 50,
      language: 'it',
      iframe: '<div id="iframe_container" style="width: 100%;position: relative;overflow: hidden;padding-top: 100%;"><iframe id="mcg" name="mcg_frame" title="Mini-Course" src="https://share.minicoursegenerator.com/thurston-cradock-test-of-shame-37299644" align="center" allow="accelerometer;autoplay;clipboard-write;encrypted-media;gyroscope;picture-in-picture" style="position: absolute;width: 100%;height: 100%;top: 0;right: 0;bottom: 0;left: 0;border: none;margin: 0px;padding: 0px;"></iframe></div>'
    }
  ];

  // Filtra i corsi per lingua corrente e ordina alfabeticamente per titolo
  const filteredCourses=courses
    .filter(course=> course.language===language)
    .sort((a,b)=> a.title.localeCompare(b.title));

  // Conta corsi per lingua
  const countByLanguage={
    en: courses.filter(course=> course.language==='en').length,
    it: courses.filter(course=> course.language==='it').length
  };

  // Funzione per gestire il click sui badge lingua
  const handleLanguageSwitch=(targetLanguage)=> {
    if (language !==targetLanguage) {
      toggleLanguage();
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{opacity: 0,y: 20}}
          animate={{opacity: 1,y: 0}}
          className="text-center mb-12"
        >
          {/* Navigation Buttons */}
          <div className="flex flex-col sm:flex-row justify-center gap-4 mb-8">
            <Link
              to="/"
              className="flex items-center text-green-600 hover:text-green-800 font-semibold transition-colors bg-white px-4 py-2 rounded-full shadow-md hover:shadow-lg"
            >
              <FaHome className="mr-2" />
              {t('backToHome')}
            </Link>
            <Link
              to="/free-resources"
              className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-4 py-2 rounded-full shadow-md hover:shadow-lg transition-colors"
            >
              <FaBook />
              <span>{t('viewFreeResources')}</span>
            </Link>
          </div>

          <h1 className="text-4xl font-bold text-gray-900 mb-4">{t('coursesTitle')}</h1>
          
          {/* Descrizione generale aggiornata */}
          <div className="max-w-4xl mx-auto mb-8">
            <h2 className="text-2xl font-semibold text-indigo-600 mb-4">
              {language==='en' ? 'Learn,Deepen and Apply COSMO Tools' : 'Impara,Approfondisci e Applica gli Strumenti COSMO'}
            </h2>
            <p className="text-lg text-gray-700 leading-relaxed">
              {language==='en' ? 'COSMO Video Courses and Live Webinars offer dynamic and engaging learning opportunities,designed to help you make the most of our tools and methodologies. Whether you are a beginner or want to deepen your expertise,our courses will provide you with the knowledge and skills needed to integrate COSMO\'s innovative assessments into your clinical practice.' : 'I Video Corsi e Webinar dal Vivo COSMO offrono opportunità di apprendimento dinamiche e coinvolgenti,progettati per aiutarti a sfruttare al meglio i nostri strumenti e metodologie. Che tu sia alle prime armi o voglia approfondire la tua expertise,i nostri corsi ti forniranno le conoscenze e le competenze necessarie per integrare le valutazioni innovative di COSMO nella tua pratica clinica.'}
            </p>
          </div>

          {/* AGGIORNATO: Badge lingua cliccabili con hover e feedback visivo */}
          <div className="flex flex-wrap justify-center gap-4 mt-6">
            <button
              onClick={()=> handleLanguageSwitch('en')}
              className={`inline-flex items-center px-4 py-2 rounded-full font-medium transition-all duration-200 transform hover:scale-105 ${
                language==='en'
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200 hover:shadow-md cursor-pointer'
              }`}
              title={language==='en' ? 'Currently viewing English courses' : 'Clicca per vedere i corsi in inglese'}
            >
              <span className="mr-2">🇺🇸</span>
              <span>
                {language==='en' ? `${countByLanguage.en} courses in English` : `${countByLanguage.en} corsi in Inglese`}
              </span>
              {language !=='en' && (
                <span className="ml-2 text-xs opacity-75">
                  {language==='en' ? 'Switch' : 'Cambia'}
                </span>
              )}
            </button>

            <button
              onClick={()=> handleLanguageSwitch('it')}
              className={`inline-flex items-center px-4 py-2 rounded-full font-medium transition-all duration-200 transform hover:scale-105 ${
                language==='it'
                  ? 'bg-indigo-600 text-white shadow-lg'
                  : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200 hover:shadow-md cursor-pointer'
              }`}
              title={language==='it' ? 'Stai visualizzando i corsi in italiano' : 'Click to view Italian courses'}
            >
              <span className="mr-2">🇮🇹</span>
              <span>
                {language==='en' ? `${countByLanguage.it} courses in Italian` : `${countByLanguage.it} corsi in Italiano`}
              </span>
              {language !=='it' && (
                <span className="ml-2 text-xs opacity-75">
                  {language==='en' ? 'Switch' : 'Cambia'}
                </span>
              )}
            </button>
          </div>

          {/* ✅ Box verde informativo sui pagamenti Stripe rimane */}
          <div className="bg-green-50 border-l-4 border-green-400 p-4 rounded-r-lg mt-6 max-w-2xl mx-auto text-left">
            <p className="text-green-800 text-sm">
              <strong>💳 {language==='en' ? 'Secure Payments:' : 'Pagamenti Sicuri:'}</strong>{' '}
              {language==='en' ? 'All courses open in a new window with secure Stripe payment processing. Your payment information is protected and never stored on our servers.' : 'Tutti i corsi si aprono in una nuova finestra con elaborazione sicura dei pagamenti Stripe. Le tue informazioni di pagamento sono protette e non vengono mai memorizzate sui nostri server.'}
            </p>
          </div>
        </motion.div>

        {/* Course Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredCourses.map(course=> (
            <CourseCard key={course.id} course={course} />
          ))}
        </div>

        {filteredCourses.length===0 && (
          <div className="text-center py-12">
            <FaGraduationCap className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              {language==='en' ? 'No courses available in your language' : 'Nessun corso disponibile nella tua lingua'}
            </h3>
            <p className="text-gray-500">
              {language==='en' ? 'Please switch to Italian to view all available courses.' : 'Si prega di passare all\'inglese per visualizzare tutti i corsi disponibili.'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Courses;